/**
 * Call-Off Communication Tab — message thread between customer and supplier.
 * Renders chat-style bubbles with role-based alignment and unread indicators.
 */
import { Loader2, Send, MessageSquare } from 'lucide-react';
import type { CallOffMessage } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

interface CallOffCommunicationTabProps {
  messages: CallOffMessage[];
  isCustomer: boolean;
  detailStatus: string;
  newMessage: string;
  setNewMessage: (v: string) => void;
  onSend: () => void;
  sending: boolean;
}

export default function CallOffCommunicationTab({
  messages, isCustomer, detailStatus, newMessage, setNewMessage, onSend, sending,
}: CallOffCommunicationTabProps) {
  const myRole = isCustomer ? 'customer' : 'supplier';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex-1 space-y-3 max-h-[450px] overflow-y-auto">
        {messages.length > 0 ? messages.map((msg) => {
          const isOtherParty = msg.sender_role !== myRole;
          const isUnread = isOtherParty && !msg.is_read;
          return (
            <div
              key={msg.id}
              className={`rounded-lg p-3 max-w-[80%] ${
                msg.sender_role === 'customer'
                  ? 'bg-blue-50 ml-0 mr-auto'
                  : 'bg-green-50 ml-auto mr-0'
              } ${isUnread ? 'ring-2 ring-blue-400' : ''}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium">{msg.sender_name}</span>
                <span className="text-xs text-muted-foreground">{msg.sender_company_name}</span>
                {isUnread && <span className="h-2 w-2 rounded-full bg-blue-500" />}
              </div>
              <p className="text-sm">{msg.message_text}</p>
              <p className="text-xs text-muted-foreground mt-1">{new Date(msg.created_at).toLocaleString()}</p>
            </div>
          );
        }) : (
          <div className="text-center py-8 text-muted-foreground">No messages yet.</div>
        )}
      </div>
      {detailStatus === 'draft' ? (
        <div className="flex items-center gap-2 border-t pt-3 text-muted-foreground text-sm">
          <MessageSquare className="h-4 w-4" />
          Messages are available once the call-off is sent to the supplier.
        </div>
      ) : (
        <div className="flex items-center gap-2 border-t pt-3">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          />
          <Button size="sm" onClick={onSend} disabled={sending || !newMessage.trim()}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}
